import { App as AntApp, Button, Switch, Tag } from 'antd';
import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	AudioOutlined,
	BarChartOutlined,
	BookOutlined,
	CarOutlined,
	CheckSquareOutlined,
	ClockCircleOutlined,
	CloudOutlined,
	CloseOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	FireOutlined,
	GiftOutlined,
	MobileOutlined,
	QrcodeOutlined,
	ReadOutlined,
	ScanOutlined,
	SaveOutlined,
	ShopOutlined,
	SoundOutlined,
	TeamOutlined,
	TrophyOutlined,
	UndoOutlined,
	UserOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent, ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRegion, type Region } from '../hooks/useRegion.js';
import {
	MobileRootPage,
	NativeIconGrid,
	NativeMirrorPage,
	NativeSection,
	NativeTopBar,
	SectionBlock,
} from '../components/MobileRootScaffold.js';

type NativeState = 'available' | 'permission' | 'not_enabled' | 'pending' | 'unknown';
type NativeGroup = 'store' | 'standard' | 'marketing' | 'data';

interface NativeAppItem {
	key: string;
	labelKey: string;
	route?: string;
	state: NativeState;
	apacState?: NativeState;
	group: NativeGroup;
	lockedVisible?: boolean;
	icon: ReactNode;
}

interface NativeAppGroup {
	key: NativeGroup;
	titleKey: string;
	items: NativeAppItem[];
}

const PRIMARY = '#FF7043';
const DISPLAY_STORAGE_KEY = 'ulucu.application.display.v1';
const DISPLAY_CHANGED_EVENT = 'ulucu.application.display.changed';

const STORE_DIGITIZATION_ITEMS: NativeAppItem[] = [
	{ key: 'customer', labelKey: 'native.application.items.customer_flow_analysis', route: '/customer', state: 'not_enabled', group: 'store', icon: <UserOutlined /> },
	{ key: 'traffic', labelKey: 'native.application.items.traffic_flow', route: '/traffic', state: 'not_enabled', group: 'store', icon: <CarOutlined /> },
	{ key: 'heatmap', labelKey: 'native.application.items.heat_map', route: '/heatmap', state: 'not_enabled', group: 'store', icon: <FireOutlined /> },
];

const STANDARDIZED_MANAGEMENT_ITEMS: NativeAppItem[] = [
	{ key: 'event', labelKey: 'native.application.items.event', route: '/event', state: 'available', group: 'standard', icon: <WarningOutlined /> },
	{ key: 'shake', labelKey: 'native.application.items.shake_inspection', state: 'not_enabled', group: 'standard', icon: <SoundOutlined /> },
	{ key: 'leavepost', labelKey: 'native.application.items.off_duty_patro', route: '/leavepost', state: 'not_enabled', group: 'standard', icon: <ClockCircleOutlined /> },
	{ key: 'inspect', labelKey: 'native.application.items.timed_inspection', route: '/inspect', state: 'available', group: 'standard', icon: <CheckSquareOutlined /> },
	{ key: 'snapshot', labelKey: 'native.application.items.timed_snapshot_inspection', route: '/m/snapshot-inspection', state: 'available', group: 'standard', icon: <ScanOutlined /> },
	{ key: 'evaluation', labelKey: 'native.application.items.assessment', route: '/evaluation', state: 'available', group: 'standard', icon: <ReadOutlined /> },
	{ key: 'scan', labelKey: 'native.application.items.qr_code_scan_superposition', route: '/scan', state: 'not_enabled', group: 'standard', icon: <QrcodeOutlined /> },
	{ key: 'staff', labelKey: 'native.application.items.intelligent_attendance', route: '/staff', state: 'not_enabled', group: 'standard', icon: <TeamOutlined /> },
	{ key: 'reward', labelKey: 'native.application.items.reward_punish', route: '/rewardpunish', state: 'not_enabled', group: 'standard', icon: <GiftOutlined /> },
	{ key: 'listening', labelKey: 'native.application.items.smart_listening', route: '/smart-listening', state: 'not_enabled', group: 'standard', icon: <AudioOutlined /> },
];

const SMART_MARKETING_ITEMS: NativeAppItem[] = [
	{ key: 'guide', labelKey: 'native.application.items.shopping_guide', route: '/m/application/native-gate/guide', state: 'not_enabled', group: 'marketing', icon: <ShopOutlined /> },
	{ key: 'classroom', labelKey: 'native.application.items.intelligent_classroom', route: '/university', state: 'not_enabled', group: 'marketing', icon: <BookOutlined /> },
];

const DATA_CENTER_ITEMS: NativeAppItem[] = [
	{ key: 'datawarn', labelKey: 'native.application.items.customer_flow_alert', route: '/datawarn', state: 'not_enabled', group: 'data', icon: <WarningOutlined /> },
];

const APAC_MENU_GROUPS: NativeAppGroup[] = [
	{ key: 'store', titleKey: 'native.application.groups.store', items: STORE_DIGITIZATION_ITEMS },
	{ key: 'standard', titleKey: 'native.application.groups.standard', items: STANDARDIZED_MANAGEMENT_ITEMS },
	{ key: 'marketing', titleKey: 'native.application.groups.marketing', items: SMART_MARKETING_ITEMS },
	{ key: 'data', titleKey: 'native.application.groups.data', items: DATA_CENTER_ITEMS },
];

const APAC_RANKING_ITEM: NativeAppItem = {
	key: 'ranking',
	labelKey: 'native.application.items.ranking_display',
	route: '/m/application/display',
	state: 'available',
	group: 'data',
	lockedVisible: true,
	icon: <BarChartOutlined />,
};

const APAC_ITEMS: NativeAppItem[] = [
	...STORE_DIGITIZATION_ITEMS,
	...STANDARDIZED_MANAGEMENT_ITEMS,
	...SMART_MARKETING_ITEMS,
	...DATA_CENTER_ITEMS,
	APAC_RANKING_ITEM,
];

const ITEMS: NativeAppItem[] = [
	{ key: 'customer', labelKey: 'native.application.items.customer_flow_analysis', route: '/customer', state: 'available', apacState: 'not_enabled', group: 'store', icon: <UserOutlined /> },
	{ key: 'traffic', labelKey: 'native.application.items.traffic_flow', route: '/traffic', state: 'available', apacState: 'not_enabled', group: 'store', icon: <CarOutlined /> },
	{ key: 'heatmap', labelKey: 'native.application.items.heat_map', route: '/heatmap', state: 'available', apacState: 'not_enabled', group: 'store', icon: <FireOutlined /> },
	{ key: 'event', labelKey: 'native.application.items.event', route: '/event', state: 'available', group: 'standard', icon: <WarningOutlined /> },
	{ key: 'shake', labelKey: 'native.application.items.shake_inspection', state: 'not_enabled', group: 'standard', icon: <SoundOutlined /> },
	{ key: 'leavepost', labelKey: 'native.application.items.off_duty_patro', route: '/leavepost', state: 'available', apacState: 'not_enabled', group: 'standard', icon: <ClockCircleOutlined /> },
	{ key: 'inspect', labelKey: 'native.application.items.timed_inspection', route: '/inspect', state: 'pending', apacState: 'available', group: 'standard', icon: <CheckSquareOutlined /> },
	{ key: 'snapshot', labelKey: 'native.application.items.timed_snapshot_inspection', route: '/m/snapshot-inspection', state: 'available', group: 'standard', icon: <ScanOutlined /> },
	{ key: 'evaluation', labelKey: 'native.application.items.assessment', route: '/evaluation', state: 'available', group: 'standard', icon: <ReadOutlined /> },
	{ key: 'scan', labelKey: 'native.application.items.qr_code_scan_superposition', route: '/scan', state: 'available', apacState: 'not_enabled', group: 'standard', icon: <QrcodeOutlined /> },
	{ key: 'staff', labelKey: 'native.application.items.intelligent_attendance', route: '/staff', state: 'available', apacState: 'not_enabled', group: 'standard', icon: <TeamOutlined /> },
	{ key: 'reward', labelKey: 'native.application.items.reward_punish', route: '/rewardpunish', state: 'available', apacState: 'not_enabled', group: 'standard', icon: <GiftOutlined /> },
	{ key: 'listening', labelKey: 'native.application.items.smart_listening', route: '/smart-listening', state: 'unknown', apacState: 'not_enabled', group: 'standard', icon: <AudioOutlined /> },
	{ key: 'phonelive', labelKey: 'native.application.items.phone_live', route: '/phonelive', state: 'available', apacState: 'not_enabled', group: 'standard', icon: <MobileOutlined /> },
	{ key: 'marketing', labelKey: 'native.application.items.smart_marketing', route: '/wechatvip', state: 'pending', group: 'marketing', icon: <TrophyOutlined /> },
	{ key: 'guide', labelKey: 'native.application.items.shopping_guide', state: 'unknown', group: 'marketing', icon: <ShopOutlined /> },
	{ key: 'classroom', labelKey: 'native.application.items.intelligent_classroom', route: '/university', state: 'pending', group: 'marketing', icon: <BookOutlined /> },
	{ key: 'datacenter', labelKey: 'native.application.items.data_center', route: '/m/analytics', state: 'available', group: 'data', icon: <BarChartOutlined /> },
	{ key: 'cloudstorage', labelKey: 'native.application.items.cloud_storage', route: '/cloudstorage', state: 'available', group: 'data', icon: <CloudOutlined /> },
	{ key: 'datawarn', labelKey: 'native.application.items.customer_flow_alert', route: '/datawarn', state: 'pending', group: 'data', icon: <WarningOutlined /> },
	{ key: 'ranking', labelKey: 'native.application.items.ranking_display', route: '/m/application/display', state: 'available', group: 'data', lockedVisible: true, icon: <BarChartOutlined /> },
];

function labels(t: TFunction) {
	return {
		displayTitle: t('native.application.display_title'),
		displaySubtitle: t('native.application.display_subtitle'),
		cancel: t('native.common.cancel'),
		complete: t('native.common.complete'),
		reset: t('native.common.reset'),
		fixed: t('native.application.fixed'),
		saved: t('native.application.saved'),
		resetDone: t('native.application.reset_done'),
		lastVisible: t('native.application.last_visible'),
		moveUp: t('native.application.move_up'),
		moveDown: t('native.application.move_down'),
		show: t('native.application.show'),
		hide: t('native.application.hide'),
		groups: {
			store: t('native.application.groups.store'),
			standard: t('native.application.groups.standard'),
			marketing: t('native.application.groups.marketing'),
			data: t('native.application.groups.data'),
		} satisfies Record<NativeGroup, string>,
	};
}

interface ApplicationDisplayPrefs {
	order: string[];
	hidden: string[];
	groupOrder: NativeGroup[];
}

function defaultDisplayPrefs(): ApplicationDisplayPrefs {
	return {
		order: ITEMS.map((item) => item.key),
		hidden: [],
		groupOrder: APAC_MENU_GROUPS.map((group) => group.key),
	};
}

function normalizeDisplayPrefs(value: unknown): ApplicationDisplayPrefs {
	const defaults = defaultDisplayPrefs();
	const raw = value && typeof value === 'object'
		? value as Partial<ApplicationDisplayPrefs>
		: {};
	const validKeys = new Set([...ITEMS, ...APAC_ITEMS].map((item) => item.key));
	const order: string[] = [];
	for (const key of Array.isArray(raw.order) ? raw.order : []) {
		if (typeof key === 'string' && validKeys.has(key) && !order.includes(key)) {
			order.push(key);
		}
	}
	for (const key of defaults.order) {
		if (!order.includes(key)) order.push(key);
	}

	const locked = new Set([...ITEMS, ...APAC_ITEMS].filter((item) => item.lockedVisible).map((item) => item.key));
	const hidden: string[] = [];
	for (const key of Array.isArray(raw.hidden) ? raw.hidden : []) {
		if (typeof key === 'string' && validKeys.has(key) && !locked.has(key) && !hidden.includes(key)) {
			hidden.push(key);
		}
	}
	const validGroupKeys = new Set<NativeGroup>(APAC_MENU_GROUPS.map((group) => group.key));
	const groupOrder: NativeGroup[] = [];
	for (const key of Array.isArray(raw.groupOrder) ? raw.groupOrder : []) {
		if (typeof key === 'string' && validGroupKeys.has(key as NativeGroup) && !groupOrder.includes(key as NativeGroup)) {
			groupOrder.push(key as NativeGroup);
		}
	}
	for (const key of defaults.groupOrder) {
		if (!groupOrder.includes(key)) groupOrder.push(key);
	}
	return { order, hidden, groupOrder };
}

function readDisplayPrefs(): ApplicationDisplayPrefs {
	try {
		const raw = localStorage.getItem(DISPLAY_STORAGE_KEY);
		return normalizeDisplayPrefs(raw ? JSON.parse(raw) : null);
	} catch {
		return defaultDisplayPrefs();
	}
}

function writeDisplayPrefs(prefs: ApplicationDisplayPrefs) {
	const normalized = normalizeDisplayPrefs(prefs);
	localStorage.setItem(DISPLAY_STORAGE_KEY, JSON.stringify(normalized));
	window.dispatchEvent(new Event(DISPLAY_CHANGED_EVENT));
}

function itemsForRegion(region: Region): NativeAppItem[] {
	if (region === 'apac') return APAC_ITEMS;
	return ITEMS;
}

function orderedItems(prefs: ApplicationDisplayPrefs, items: NativeAppItem[] = ITEMS): NativeAppItem[] {
	const byKey = new Map(items.map((item) => [item.key, item]));
	const result: NativeAppItem[] = [];
	for (const key of prefs.order) {
		const item = byKey.get(key);
		if (item) result.push(item);
	}
	for (const item of items) {
		if (!result.some((entry) => entry.key === item.key)) result.push(item);
	}
	return result;
}

function visibleItems(prefs: ApplicationDisplayPrefs, items: NativeAppItem[] = ITEMS): NativeAppItem[] {
	const hidden = new Set(prefs.hidden);
	return orderedItems(prefs, items).filter((item) => item.lockedVisible || !hidden.has(item.key));
}

function apacGroupsForPrefs(prefs: ApplicationDisplayPrefs): NativeAppGroup[] {
	const byKey = new Map(APAC_MENU_GROUPS.map((group) => [group.key, group]));
	const result: NativeAppGroup[] = [];
	for (const key of prefs.groupOrder) {
		const group = byKey.get(key);
		if (group) result.push(group);
	}
	for (const group of APAC_MENU_GROUPS) {
		if (!result.some((entry) => entry.key === group.key)) result.push(group);
	}
	return result;
}

export default function ApplicationTab() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const region = useRegion();
	const regionItems = useMemo(() => itemsForRegion(region), [region]);
	const [prefs, setPrefs] = useState(readDisplayPrefs);

	useEffect(() => {
		const sync = () => setPrefs(readDisplayPrefs());
		window.addEventListener('storage', sync);
		window.addEventListener(DISPLAY_CHANGED_EVENT, sync);
		return () => {
			window.removeEventListener('storage', sync);
			window.removeEventListener(DISPLAY_CHANGED_EVENT, sync);
		};
	}, []);

	const openItem = (item: NativeAppItem) => {
		if (!item.route || item.state !== 'available') {
			navigate(`/m/application/native-gate/${item.key}`);
			return;
		}
		navigate(item.route);
	};

	if (region === 'apac') {
		const groups = apacGroupsForPrefs(prefs);
		return (
			<NativeMirrorPage>
				<NativeTopBar title={t('native.application.title')} />
				{groups.map((group) => (
					<NativeSection key={group.key} title={t(group.titleKey)}>
						<NativeIconGrid
							items={group.items.map((item) => ({
								key: item.key,
								title: t(item.labelKey),
								icon: item.icon,
								muted: item.state !== 'available' || !item.route,
								onClick: () => openItem(item),
							}))}
						/>
					</NativeSection>
				))}
				<div style={{ padding: '18px 42px 112px' }}>
					<button
						type="button"
						onClick={() => navigate(APAC_RANKING_ITEM.route!)}
						style={{
							width: '100%',
							border: 0,
							borderRadius: 22,
							background: PRIMARY,
							color: '#fff',
							minHeight: 44,
							fontSize: 15,
							fontWeight: 600,
							cursor: 'pointer',
						}}
					>
						{t(APAC_RANKING_ITEM.labelKey)}
					</button>
				</div>
			</NativeMirrorPage>
		);
	}

	const displayItems = visibleItems(prefs, regionItems);
	return (
		<NativeMirrorPage>
			<NativeTopBar title={t('native.application.title')} />
			<NativeSection>
				<NativeIconGrid
					items={displayItems.map((item) => ({
						key: item.key,
						title: t(item.labelKey),
						icon: item.icon,
						muted: item.state !== 'available' || !item.route,
						onClick: () => openItem(item),
					}))}
				/>
			</NativeSection>
		</NativeMirrorPage>
	);
}

export function ApplicationNativeGate() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<NativeMirrorPage>
			<NativeTopBar
				title={t('native.application.function_intro')}
				onBack={() => navigate('/m/application', { replace: true })}
			/>
			<div style={{ minHeight: 260, display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center' }}>
				<div style={{ color: '#4f5661', fontSize: 15, lineHeight: 1.45 }}>
					{t('native.application.permission_denied')}
				</div>
			</div>
		</NativeMirrorPage>
	);
}

export function ApplicationDisplaySettings() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { message: messageApi } = AntApp.useApp();
	const region = useRegion();
	const text = useMemo(() => labels(t), [t]);
	const [prefs, setPrefs] = useState(readDisplayPrefs);
	const regionItems = useMemo(() => itemsForRegion(region), [region]);
	const items = useMemo(() => orderedItems(prefs, regionItems), [prefs, regionItems]);
	const hidden = useMemo(() => new Set(prefs.hidden), [prefs.hidden]);
	const apacGroups = useMemo(() => apacGroupsForPrefs(prefs), [prefs]);
	const groupRefs = useRef(new Map<NativeGroup, HTMLDivElement | null>());
	const draggingGroupRef = useRef<NativeGroup | null>(null);

	const moveItem = (key: string, delta: -1 | 1) => {
		setPrefs((current) => {
			const order = [...current.order];
			const index = order.indexOf(key);
			const nextIndex = index + delta;
			if (index < 0 || nextIndex < 0 || nextIndex >= order.length) return current;
			const [removed] = order.splice(index, 1);
			order.splice(nextIndex, 0, removed);
			return { ...current, order };
		});
	};

	const toggleItem = (item: NativeAppItem) => {
		if (item.lockedVisible) return;
		setPrefs((current) => {
			const hiddenSet = new Set(current.hidden);
			const isHidden = hiddenSet.has(item.key);
			const currentVisibleCount = orderedItems(current, regionItems)
				.filter((entry) => entry.lockedVisible || !hiddenSet.has(entry.key))
				.length;
			if (!isHidden && currentVisibleCount <= 1) {
				messageApi.warning(text.lastVisible);
				return current;
			}
			if (isHidden) hiddenSet.delete(item.key);
			else hiddenSet.add(item.key);
			return { ...current, hidden: Array.from(hiddenSet) };
		});
	};

	const reset = () => {
		setPrefs(defaultDisplayPrefs());
		if (region !== 'apac') messageApi.success(text.resetDone);
	};

	const complete = () => {
		writeDisplayPrefs(prefs);
		if (region !== 'apac') messageApi.success(text.saved);
		navigate('/m/application', { replace: true });
	};

	const reorderApacGroup = (fromKey: NativeGroup, toKey: NativeGroup) => {
		if (fromKey === toKey) return;
		setPrefs((current) => {
			const order = apacGroupsForPrefs(current).map((group) => group.key);
			const fromIndex = order.indexOf(fromKey);
			const toIndex = order.indexOf(toKey);
			if (fromIndex < 0 || toIndex < 0) return current;
			const [removed] = order.splice(fromIndex, 1);
			order.splice(toIndex, 0, removed);
			return { ...current, groupOrder: order };
		});
	};

	const findApacGroupAt = (clientY: number): NativeGroup | null => {
		for (const [key, node] of groupRefs.current.entries()) {
			if (!node) continue;
			const rect = node.getBoundingClientRect();
			if (clientY >= rect.top && clientY <= rect.bottom) return key;
		}
		return null;
	};

	const startApacGroupDrag = (event: PointerEvent<HTMLDivElement>, key: NativeGroup) => {
		draggingGroupRef.current = key;
		event.currentTarget.setPointerCapture?.(event.pointerId);
	};

	const moveApacGroupDrag = (event: PointerEvent<HTMLDivElement>) => {
		const active = draggingGroupRef.current;
		if (!active) return;
		const target = findApacGroupAt(event.clientY);
		if (target && target !== active) {
			reorderApacGroup(active, target);
			draggingGroupRef.current = target;
		}
	};

	const endApacGroupDrag = (event: PointerEvent<HTMLDivElement>) => {
		draggingGroupRef.current = null;
		event.currentTarget.releasePointerCapture?.(event.pointerId);
	};

	if (region === 'apac') {
		return (
			<NativeMirrorPage style={{ background: '#f6f6f6', padding: 0 }}>
				<header
					style={{
						height: 48,
						background: '#fff',
						borderBottom: '1px solid #eeeeee',
						display: 'grid',
						gridTemplateColumns: '84px minmax(0, 1fr) 84px',
						alignItems: 'center',
						position: 'sticky',
						top: 0,
						zIndex: 10,
					}}
				>
					<Button type="text" onClick={() => navigate('/m/application', { replace: true })} style={{ color: '#30343b' }}>
						{text.cancel}
					</Button>
					<div style={{ textAlign: 'center', color: '#20242b', fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>
						{t(APAC_RANKING_ITEM.labelKey)}
					</div>
					<Button type="text" onClick={complete} style={{ color: PRIMARY, fontWeight: 650 }}>
						{text.complete}
					</Button>
				</header>

				<div style={{ padding: '0 0 24px' }}>
					<div style={{ padding: '14px 16px 10px', color: '#30343b', fontSize: 15, background: '#f6f6f6' }}>
						{text.displaySubtitle}
					</div>
					<div style={{ background: '#fff', borderTop: '1px solid #eeeeee', borderBottom: '1px solid #eeeeee' }}>
						{apacGroups.map((group, index) => (
							<div
								key={group.key}
								ref={(node) => {
									groupRefs.current.set(group.key, node);
								}}
								onPointerDown={(event) => startApacGroupDrag(event, group.key)}
								onPointerMove={moveApacGroupDrag}
								onPointerUp={endApacGroupDrag}
								onPointerCancel={endApacGroupDrag}
								style={{
									minHeight: 55,
									display: 'grid',
									gridTemplateColumns: 'minmax(0, 1fr) 30px',
									alignItems: 'center',
									gap: 12,
									padding: '0 16px',
									borderTop: index === 0 ? 0 : '1px solid #eeeeee',
									color: '#20242b',
									fontSize: 15,
									background: '#fff',
									touchAction: 'none',
									cursor: 'grab',
									userSelect: 'none',
								}}
							>
								<div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t(group.titleKey)}</div>
								<div
									aria-hidden="true"
									style={{
										width: 20,
										height: 18,
										justifySelf: 'end',
										background: 'repeating-linear-gradient(to bottom, #a5a5a5 0, #a5a5a5 2px, transparent 2px, transparent 6px)',
										opacity: 0.82,
									}}
								/>
							</div>
						))}
					</div>
				</div>
			</NativeMirrorPage>
		);
	}

	return (
		<MobileRootPage>
			<header style={{
				height: 52,
				display: 'grid',
				gridTemplateColumns: '96px 1fr 96px',
				alignItems: 'center',
				gap: 8,
				margin: '0 0 12px',
			}}>
				<Button type="text" icon={<CloseOutlined />} onClick={() => navigate('/m/application', { replace: true })}>
					{text.cancel}
				</Button>
				<div style={{ textAlign: 'center', minWidth: 0 }}>
					<div style={{ fontSize: 16, fontWeight: 600, color: '#111827', lineHeight: 1.2 }}>{text.displayTitle}</div>
					<div style={{ fontSize: 12, color: '#697386', marginTop: 2, lineHeight: 1.2 }}>{text.displaySubtitle}</div>
				</div>
				<Button type="primary" icon={<SaveOutlined />} onClick={complete}>
					{text.complete}
				</Button>
			</header>

			<SectionBlock
				extra={
					<Button size="small" icon={<UndoOutlined />} onClick={reset}>
						{text.reset}
					</Button>
				}
			>
				<div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
					{items.map((item, index) => {
						const isHidden = hidden.has(item.key) && !item.lockedVisible;
						return (
							<div
								key={item.key}
								style={{
									display: 'grid',
									gridTemplateColumns: 'minmax(0, 1fr) auto',
									gap: 12,
									alignItems: 'center',
									minHeight: 72,
									padding: '10px 12px',
									borderTop: index === 0 ? 0 : '1px solid #eef0f3',
									opacity: isHidden ? 0.58 : 1,
								}}
							>
								<div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
									<div style={{
										width: 38,
										height: 38,
										borderRadius: 8,
										background: isHidden ? '#f1f5f9' : '#fff0e8',
										color: isHidden ? '#64748b' : PRIMARY,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: 20,
										flex: '0 0 auto',
									}}>
										{item.icon}
									</div>
									<div style={{ minWidth: 0, flex: 1 }}>
										<div style={{ color: '#111827', fontSize: 14, fontWeight: 500, lineHeight: 1.25 }}>
											{t(item.labelKey)}
										</div>
										<div style={{ color: '#697386', fontSize: 12, marginTop: 3, lineHeight: 1.25 }}>
											{text.groups[item.group]}
										</div>
										<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
											<Tag color="default" style={{ margin: 0 }}>{text.groups[item.group]}</Tag>
											{item.lockedVisible && <Tag color="processing" style={{ margin: 0 }}>{text.fixed}</Tag>}
										</div>
									</div>
								</div>
								<div style={{ display: 'grid', gridTemplateColumns: '32px 32px 56px', gap: 6, alignItems: 'center' }}>
									<Button
										aria-label={text.moveUp}
										icon={<ArrowUpOutlined />}
										disabled={index === 0}
										onClick={() => moveItem(item.key, -1)}
									/>
									<Button
										aria-label={text.moveDown}
										icon={<ArrowDownOutlined />}
										disabled={index === items.length - 1}
										onClick={() => moveItem(item.key, 1)}
									/>
									<Switch
										checked={!isHidden}
										disabled={item.lockedVisible}
										checkedChildren={<EyeOutlined />}
										unCheckedChildren={<EyeInvisibleOutlined />}
										title={!isHidden ? text.hide : text.show}
										onChange={() => toggleItem(item)}
									/>
								</div>
							</div>
						);
					})}
				</div>
			</SectionBlock>
		</MobileRootPage>
	);
}
